import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';

interface Job {
  id: number;
  system_type: string;
  status: string;
  progress: number;
  created_at: string;
  settings?: Record<string, any>;
}

interface ProjectDetails {
  id: number;
  client_name: string;
  name: string;
  description: string;
  created_at: string;
  jobs: Job[];
  total_jobs: number;
  completed_jobs: number;
  in_progress_jobs: number;
  failed_jobs: number;
  factsagent_count: number;
  topicalmap_count: number;
  cppages_count: number;
  images_count: number;
  content_count: number;
  servicearea_count: number;
}

interface ProjectDashboardProps {
  projectId: number;
  onBack: () => void;
}

const SYSTEM_ROUTES: Record<string, string> = {
  factsagent: '/factsagent',
  topicalmap: '/topicalmap',
  content: '/content',
  briefcontent: '/briefcontent',
  contentframework: '/contentframework',
  contentframeworkv2: '/contentframeworkv2',
  liveupdate: '/liveupdate',
  payloadagent: '/payloadagent',
  imagegenerator: '/imagegenerator',
};

const SYSTEM_NAMES: Record<string, string> = {
  factsagent: 'FactsAgent',
  topicalmap: 'Topical Map',
  content: 'Content',
  briefcontent: 'Brief Content',
  contentframework: 'Content Framework',
  contentframeworkv2: 'Content Framework v2',
  liveupdate: 'WP Live Update',
  payloadagent: 'Live Payload Update',
  imagegenerator: 'Image Generator',
};

const getJobTitle = (job: Job): string => {
  const settings = job.settings || {};

  switch (job.system_type) {
    case 'factsagent':
      if (settings.filename) {
        return settings.filename.replace(/\.(txt|json)$/i, '');
      }
      return `Fact Sheet ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'topicalmap':
      const generators = settings.generators as string[] || [];
      if (generators.length > 0) {
        return generators.slice(0, 2).join(', ') + (generators.length > 2 ? '...' : '');
      }
      return `Topical Map ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'cppages':
      const cpGenerators = settings.generators as string[] || [];
      if (cpGenerators.length > 0) {
        return cpGenerators.slice(0, 2).join(', ') + (cpGenerators.length > 2 ? '...' : '');
      }
      return `CP Pages ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'images':
      if (settings.template) {
        return `${settings.template.charAt(0).toUpperCase() + settings.template.slice(1)} Template`;
      }
      return `Images ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'content':
      const articleTypes = settings.article_types as string[] || [];
      if (articleTypes.length > 0) {
        return `${articleTypes.length} Article Type${articleTypes.length > 1 ? 's' : ''}`;
      }
      return `Content ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'serviceareapages':
      if (settings.facts_file) {
        return settings.facts_file.replace(/\.(txt|json)$/i, '');
      }
      return `Service Areas ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    default:
      return `${SYSTEM_NAMES[job.system_type] || job.system_type} ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
};

export const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  projectId,
  onBack,
}) => {
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingJobs, setCancellingJobs] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  // Auto-refresh dashboard when there are in-progress jobs
  useEffect(() => {
    if (!project || project.in_progress_jobs === 0) return;

    const interval = setInterval(() => {
      fetchProjectDetails();
    }, 5000);

    return () => clearInterval(interval);
  }, [project?.in_progress_jobs, projectId]);

  const handleCancelJob = async (jobId: number) => {
    if (cancellingJobs.has(jobId)) return;

    try {
      setCancellingJobs(prev => new Set(prev).add(jobId));
      await api.post(`/jobs/${jobId}/cancel`);
      // Refresh project details
      fetchProjectDetails();
    } catch (err: any) {
      console.error('Failed to cancel job:', err);
      alert(err.response?.data?.detail || 'Failed to cancel job');
    } finally {
      setCancellingJobs(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (err) {
      setError('Failed to load project details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const viewAnalytics = () => {
    navigate(`/analytics/${projectId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner text-brand-600 w-8 h-8"></div>
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="empty-state">
            <svg className="empty-state-icon text-danger-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="empty-state-title text-danger-600">{error || 'Project not found'}</h3>
            <button onClick={onBack} className="btn-secondary mt-4">
              Back to Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Project Header */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-2xl">
                  {project.client_name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{project.client_name}</h1>
                <p className="text-brand-600 font-medium text-lg mt-1">{project.name}</p>
                {project.description && (
                  <p className="text-gray-500 mt-2 max-w-2xl">{project.description}</p>
                )}
                <p className="text-gray-400 text-sm mt-3">
                  Created {new Date(project.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={viewAnalytics} className="btn-secondary">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Analytics
              </button>
              <button className="btn-ghost">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <p className="stat-card-label">Total Jobs</p>
          <p className="stat-card-value">{project.total_jobs}</p>
        </div>
        <div className="stat-card border-l-4 border-success-500">
          <p className="stat-card-label">Completed</p>
          <p className="stat-card-value text-success-600">{project.completed_jobs}</p>
        </div>
        <div className="stat-card border-l-4 border-warning-500">
          <p className="stat-card-label">In Progress</p>
          <p className="stat-card-value text-warning-600">{project.in_progress_jobs}</p>
        </div>
        <div className="stat-card border-l-4 border-danger-500">
          <p className="stat-card-label">Failed</p>
          <p className="stat-card-value text-danger-600">{project.failed_jobs}</p>
        </div>
      </div>

      {/* Recent Jobs */}
      {project.jobs && project.jobs.length > 0 && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
              <p className="text-sm text-gray-500 mt-1">Latest jobs and their status</p>
            </div>
            <Link to={`/projects/${project.id}/activities`} className="btn-ghost btn-sm">
              View All
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="divide-y divide-gray-100">
            {project.jobs.slice(0, 5).map((job) => (
              <div key={job.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    job.status === 'completed' ? 'bg-success-100' :
                    job.status === 'processing' ? 'bg-warning-100' :
                    job.status === 'failed' ? 'bg-danger-100' : 'bg-gray-100'
                  }`}>
                    {job.status === 'completed' ? (
                      <svg className="w-5 h-5 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : job.status === 'processing' ? (
                      <div className="spinner text-warning-600 w-5 h-5"></div>
                    ) : job.status === 'failed' ? (
                      <svg className="w-5 h-5 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {getJobTitle(job)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {SYSTEM_NAMES[job.system_type] || job.system_type}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  {/* Progress */}
                  <div className="w-32">
                    <div className="progress-bar">
                      <div
                        className={`progress-bar-fill ${
                          job.status === 'completed' ? 'bg-success-500' :
                          job.status === 'failed' ? 'bg-danger-500' : 'bg-brand-500'
                        }`}
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">{job.progress}%</p>
                  </div>

                  {/* Status Badge */}
                  <span className={`${
                    job.status === 'completed' ? 'badge-success' :
                    job.status === 'processing' ? 'badge-warning' :
                    job.status === 'failed' ? 'badge-danger' : 'badge-gray'
                  }`}>
                    {job.status}
                  </span>

                  {/* Cancel Button - only for processing/pending jobs */}
                  {(job.status === 'processing' || job.status === 'pending') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelJob(job.id);
                      }}
                      disabled={cancellingJobs.has(job.id)}
                      className="btn-secondary btn-sm text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50"
                      title="Cancel this job"
                    >
                      {cancellingJobs.has(job.id) ? (
                        <div className="spinner w-4 h-4"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      Cancel
                    </button>
                  )}

                  {/* Open Button - navigates to specific job */}
                  <button
                    onClick={() => navigate(`${SYSTEM_ROUTES[job.system_type] || '/'}?project_id=${project.id}&job_id=${job.id}`)}
                    className="btn-secondary btn-sm"
                    title={`Open ${job.system_type.charAt(0).toUpperCase() + job.system_type.slice(1)}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
